import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, TrendingUp, Users,
  Wifi, Activity, Download, AlertCircle, CheckCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie,
  Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VisualAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [bandwidthData, setBandwidthData] = useState(null);
  const [customerGrowthData, setCustomerGrowthData] = useState(null);
  const [networkHealthData, setNetworkHealthData] = useState(null);

  // Colors for charts
  const COLORS = {
    primary: '#3b82f6',
    secondary: '#8b5cf6',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#06b6d4'
  };

  useEffect(() => {
    fetchAllAnalytics();
  }, []);

  const fetchAllAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('monitoringAdminToken');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      console.log('🔍 Fetching analytics data...');

      // Fetch bandwidth analytics
      const bandwidthResponse = await fetch(`${API_URL}/api/monitoring/analytics/bandwidth?days=30`, { headers });
      console.log('📊 Bandwidth response status:', bandwidthResponse.status);
      
      if (!bandwidthResponse.ok) {
        const errorText = await bandwidthResponse.text();
        console.error('❌ Bandwidth fetch failed:', errorText);
        throw new Error(`Bandwidth API failed: ${bandwidthResponse.status}`);
      }
      const bandwidth = await bandwidthResponse.json();
      console.log('✅ Bandwidth data:', bandwidth);
      
      // Fetch customer growth analytics
      const growthResponse = await fetch(`${API_URL}/api/monitoring/analytics/customer-growth?months=12`, { headers });
      console.log('📊 Growth response status:', growthResponse.status);
      
      if (!growthResponse.ok) {
        const errorText = await growthResponse.text();
        console.error('❌ Growth fetch failed:', errorText);
        throw new Error(`Growth API failed: ${growthResponse.status}`);
      }
      const growth = await growthResponse.json();
      console.log('✅ Growth data:', growth);
      
      // Fetch network health analytics
      const healthResponse = await fetch(`${API_URL}/api/monitoring/analytics/network-health`, { headers });
      console.log('📊 Health response status:', healthResponse.status);
      
      if (!healthResponse.ok) {
        const errorText = await healthResponse.text();
        console.error('❌ Health fetch failed:', errorText);
        throw new Error(`Health API failed: ${healthResponse.status}`);
      }
      const health = await healthResponse.json();
      console.log('✅ Health data:', health);

      setBandwidthData(bandwidth);
      setCustomerGrowthData(growth);
      setNetworkHealthData(health);
      
      console.log('🎉 All analytics data loaded successfully!');
      setLoading(false);
    } catch (error) {
      console.error('❌ Error fetching analytics:', error);
      toast.error('Failed to load analytics data: ' + error.message);
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllAnalytics();
    setRefreshing(false);
    toast.success('Analytics refreshed');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Activity className="h-12 w-12 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 p-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/monitoring-dashboard')}
              variant="ghost"
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </Button>
            {/* Netix Logo */}
            <div className="bg-white rounded-lg p-2 shadow-md">
              <img 
                src="/netix-logo-horizontal.png" 
                alt="Netix Fibernet" 
                className="h-8 w-auto"
              />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Visual Analytics Dashboard</h1>
              <p className="text-purple-100">Bandwidth, customer growth & network health insights</p>
            </div>
          </div>
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            className="bg-white text-purple-600 hover:bg-purple-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {/* Total Customers */}
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Customers</p>
                <p className="text-3xl font-bold text-gray-900">
                  {customerGrowthData?.total_customers || 0}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  <TrendingUp className="h-3 w-3 inline mr-1" />
                  {customerGrowthData?.total_new || 0} new this year
                </p>
              </div>
              <Users className="h-12 w-12 text-blue-500 opacity-20" />
            </div>
          </CardContent>
        </Card>

        {/* Total Bandwidth Usage */}
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Bandwidth (30d)</p>
                <p className="text-3xl font-bold text-gray-900">
                  {bandwidthData?.total_bandwidth_gb?.toFixed(2) || 0} GB
                </p>
                <p className="text-xs text-gray-500 mt-1">Last 30 days</p>
              </div>
              <Download className="h-12 w-12 text-purple-500 opacity-20" />
            </div>
          </CardContent>
        </Card>

        {/* Network Uptime */}
        <Card className="border-l-4 border-l-cyan-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Network Uptime</p>
                <p className="text-3xl font-bold text-gray-900">
                  {networkHealthData?.uptime_percentage?.toFixed(2) || 0}%
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {networkHealthData?.active_customers || 0} active connections
                </p>
              </div>
              <Wifi className="h-12 w-12 text-cyan-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bandwidth Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Daily Bandwidth Trends */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-purple-600" />
              Daily Bandwidth Usage (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={bandwidthData?.daily_trends || []}>
                <defs>
                  <linearGradient id="colorUsage" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Area 
                  type="monotone" 
                  dataKey="usage_gb" 
                  stroke={COLORS.primary} 
                  fillOpacity={1} 
                  fill="url(#colorUsage)" 
                  name="Usage (GB)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Bandwidth Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-purple-600" />
              Top Users - Bandwidth Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={bandwidthData?.distribution || []}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value}GB`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {(bandwidthData?.distribution || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={Object.values(COLORS)[index % Object.values(COLORS).length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Customer Growth Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Monthly Customer Growth */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              Customer Growth Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={customerGrowthData?.monthly_growth || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="total" 
                  stroke={COLORS.primary} 
                  strokeWidth={2}
                  name="Total Customers"
                />
                <Line 
                  type="monotone" 
                  dataKey="new" 
                  stroke={COLORS.success} 
                  strokeWidth={2}
                  name="New"
                />
                <Line 
                  type="monotone" 
                  dataKey="churned" 
                  stroke={COLORS.danger} 
                  strokeWidth={2}
                  name="Churned"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Customer Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-blue-600" />
              Customer Status Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={customerGrowthData?.status_distribution || []}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {(customerGrowthData?.status_distribution || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Network Health Metrics */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wifi className="h-5 w-5 text-cyan-600" />
            Network Health Performance Metrics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {(networkHealthData?.performance_metrics || []).map((metric, index) => (
              <div 
                key={index}
                className={`p-4 rounded-lg border-2 ${
                  metric.status === 'good' 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-orange-50 border-orange-200'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-gray-700">{metric.name}</p>
                  {metric.status === 'good' ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-orange-600" />
                  )}
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {metric.value}{metric.unit}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Target: {metric.target}{metric.unit}
                </p>
                {/* Progress bar */}
                <div className="mt-2 bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      metric.status === 'good' ? 'bg-green-500' : 'bg-orange-500'
                    }`}
                    style={{ 
                      width: `${Math.min((metric.value / metric.target) * 100, 100)}%` 
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Additional Network Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600">SLA Status</p>
              <p className="text-xl font-bold text-gray-900 capitalize">
                {networkHealthData?.sla_status || 'N/A'}
              </p>
              <p className="text-xs text-gray-500">
                {networkHealthData?.sla_actual?.toFixed(2)}% / {networkHealthData?.sla_target}% target
              </p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-gray-600">Active Alerts</p>
              <p className="text-xl font-bold text-gray-900">
                {networkHealthData?.active_alerts || 0}
              </p>
              <p className="text-xs text-gray-500">
                {networkHealthData?.recent_incidents || 0} incidents (last 7 days)
              </p>
            </div>
            <div className="p-4 bg-cyan-50 rounded-lg">
              <p className="text-sm text-gray-600">Average Health Score</p>
              <p className="text-xl font-bold text-gray-900">
                {networkHealthData?.avg_health_score?.toFixed(1) || 0}/100
              </p>
              <p className="text-xs text-gray-500">Overall network quality</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VisualAnalyticsDashboard;
